import React from 'react';
import PropTypes from 'prop-types';
import RemoveButton from '../shared/RemoveButton';
import './SinglePatch.css';

/**
 * SinglePatch — Configurator
 *
 * 36px tile for one placed patch. Selecting it reveals a black remove button
 * centred on its top-right corner.
 *
 * Figma component set: 4275:22815
 * Variants: State = Default (4275:22790) | Hover (4275:22791) | Selected (4275:22792)
 *
 * Hover is CSS-only; Selected comes from `selected`. The remove button
 * renders only while selected and when `onRemove` is set. `size="large"`
 * is the OVER/LL "Min patchar" tray tile: 52px from 1024px up, 36px below.
 */
const SinglePatch = ({
  image,
  imageAlt = '',
  selected = false,
  size = 'default',
  onClick,
  onRemove,
  removeLabel = 'Remove',
  className = '',
}) => {
  const classes = [
    'cfg-single-patch',
    size === 'large' && 'cfg-single-patch--large',
    selected && 'cfg-single-patch--selected',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      <button
        type="button"
        className="cfg-single-patch__tile"
        onClick={onClick}
        aria-pressed={selected}
      >
        {image && <img className="cfg-single-patch__img" src={image} alt={imageAlt} />}
      </button>

      {selected && onRemove && <RemoveButton onClick={onRemove} label={removeLabel} />}
    </div>
  );
};

SinglePatch.propTypes = {
  /** Patch image URL (contained with a 12.5% inset) */
  image: PropTypes.string,
  /** Alt text — also the tile button's accessible name */
  imageAlt: PropTypes.string,
  /** Selected state — white tile, gray-10 border, remove button */
  selected: PropTypes.bool,
  /** Tile size — 'default' 36px, 'large' 52px from 1024px up (36px below) */
  size: PropTypes.oneOf(['default', 'large']),
  /** Tile click handler */
  onClick: PropTypes.func,
  /** Remove handler — shows the remove button while selected */
  onRemove: PropTypes.func,
  /** Accessible name of the remove button */
  removeLabel: PropTypes.string,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default SinglePatch;
